/**
 * Theme Configuration
 * 
 * Defines all color palettes for the 4 available themes:
 * - Aurora (default): Purple/blue gradient
 * - Forest: Green with pastel yellow/orange accents
 * - Ocean: Deep blue
 * - Sunset: Warm orange/pink
 */

export const themes = {
  aurora: {
    name: 'Aurora',
    description: 'Modern purple and blue gradient theme',
    primary: '#7B61FF',
    primaryDark: '#6C5DD3',
    primaryLight: '#9B7FFF',
    primary50: '#F5F3FF',
    primary100: '#EDE9FE',
    secondary: '#6366F1',
    secondaryDark: '#4F46E5',
    accent: '#8B5CF6',
    backgroundGradient: {
      from: '#67E8F9',      // cyan-300
      via: '#60A5FA',       // blue-400 at 80% opacity
      to: '#A855F7',        // purple-500 at 70% opacity
    },
    text: {
      primary: '#111827',
      secondary: '#6B7280',
      light: '#9CA3AF',
      onPrimary: '#FFFFFF',
    },
    background: {
      primary: '#FFFFFF',
      secondary: '#F9FAFB',
      tertiary: '#F3F4F6',
      overlay: 'rgba(255, 255, 255, 0.9)',
    },
    border: {
      primary: '#E5E7EB',
      secondary: '#D1D5DB',
      focus: '#7B61FF',
    },
    interactive: {
      hoverPrimary: '#6C5DD3',
      hoverBg: '#F5F3FF',
      activePrimary: '#5A4BC2',
      focusRing: 'rgba(123, 97, 255, 0.2)',
    },
    status: {
      success: '#10B981',
      error: '#EF4444',
      warning: '#F59E0B',
      info: '#3B82F6',
    },
  },
  
  forest: {
    name: 'Forest',
    description: 'Natural green theme with warm pastel accents',
    primary: '#10B981',
    primaryDark: '#059669',
    primaryLight: '#34D399',
    primary50: '#ECFDF5',
    primary100: '#D1FAE5',
    secondary: '#14B8A6',
    secondaryDark: '#0D9488',
    accent: '#22C55E',
    accentWarm: '#FDE68A',        // Pastel yellow/orange
    accentWarmLight: '#FEF3C7',   // Very light pastel yellow
    backgroundGradient: {
      from: '#BBF7D0',      // green-200
      via: '#6EE7B7',       // emerald-300 at 80% opacity
      to: '#2DD4BF',        // teal-400 at 70% opacity
      accent: '#FDE68A',    // Optional pastel yellow/orange in gradient
    },
    text: {
      primary: '#111827',
      secondary: '#6B7280',
      light: '#9CA3AF',
      onPrimary: '#FFFFFF',
    },
    background: {
      primary: '#FFFFFF',
      secondary: '#F0FDF4',
      tertiary: '#ECFDF5',
      warmAccent: '#FEF9E7',      // Very subtle pastel yellow/orange tint
      overlay: 'rgba(255, 255, 255, 0.9)',
    },
    border: {
      primary: '#D1FAE5',
      secondary: '#A7F3D0',
      focus: '#10B981',
    },
    interactive: {
      hoverPrimary: '#059669',
      hoverBg: '#ECFDF5',
      hoverWarm: '#FEF3C7',       // Subtle yellow/orange hover accent
      activePrimary: '#047857',
      focusRing: 'rgba(16, 185, 129, 0.2)',
    },
    status: {
      success: '#10B981',
      error: '#EF4444',
      warning: '#F59E0B',
      info: '#14B8A6',
    },
  },
  
  ocean: {
    name: 'Ocean',
    description: 'Deep blue ocean theme - trust and stability',
    primary: '#3B82F6',
    primaryDark: '#2563EB',
    primaryLight: '#60A5FA',
    primary50: '#EFF6FF',
    primary100: '#DBEAFE',
    secondary: '#06B6D4',
    secondaryDark: '#0891B2',
    accent: '#0EA5E9',
    backgroundGradient: {
      from: '#BFDBFE',      // blue-200
      via: '#93C5FD',       // blue-300 at 80% opacity
      to: '#60A5FA',        // blue-400 at 70% opacity
    },
    text: {
      primary: '#111827',
      secondary: '#6B7280',
      light: '#9CA3AF',
      onPrimary: '#FFFFFF',
    },
    background: {
      primary: '#FFFFFF',
      secondary: '#F0F9FF',
      tertiary: '#EFF6FF',
      overlay: 'rgba(255, 255, 255, 0.9)',
    },
    border: {
      primary: '#DBEAFE',
      secondary: '#BFDBFE',
      focus: '#3B82F6',
    },
    interactive: {
      hoverPrimary: '#2563EB',
      hoverBg: '#EFF6FF',
      activePrimary: '#1D4ED8',
      focusRing: 'rgba(59, 130, 246, 0.2)',
    },
    status: {
      success: '#10B981',
      error: '#EF4444',
      warning: '#F59E0B',
      info: '#3B82F6',
    },
  },
  
  sunset: {
    name: 'Sunset',
    description: 'Warm orange and pink gradient theme',
    primary: '#F59E0B',
    primaryDark: '#D97706',
    primaryLight: '#FBBF24',
    primary50: '#FFFBEB',
    primary100: '#FEF3C7',
    secondary: '#EC4899',
    secondaryDark: '#DB2777',
    accent: '#F97316',
    backgroundGradient: {
      from: '#FED7AA',      // orange-200
      via: '#FBCFE8',       // pink-300 at 80% opacity
      to: '#FB7185',        // rose-400 at 70% opacity
    },
    text: {
      primary: '#111827',
      secondary: '#6B7280',
      light: '#9CA3AF',
      onPrimary: '#FFFFFF',
    },
    background: {
      primary: '#FFFFFF',
      secondary: '#FFF7ED',
      tertiary: '#FFFBEB',
      overlay: 'rgba(255, 255, 255, 0.9)',
    },
    border: {
      primary: '#FEF3C7',
      secondary: '#FDE68A',
      focus: '#F59E0B',
    },
    interactive: {
      hoverPrimary: '#D97706',
      hoverBg: '#FFFBEB',
      activePrimary: '#B45309',
      focusRing: 'rgba(245, 158, 11, 0.2)',
    },
    status: {
      success: '#10B981',
      error: '#EF4444',
      warning: '#F59E0B',
      info: '#3B82F6',
    },
  },
};

/**
 * Get theme by name
 * @param {string} themeName - Theme identifier
 * @returns {Object} Theme configuration object
 */
export const getTheme = (themeName = 'aurora') => {
  return themes[themeName] || themes.aurora;
};

/**
 * Get all available theme names
 * @returns {string[]} Array of theme identifiers
 */
export const getThemeNames = () => {
  return Object.keys(themes);
};

/**
 * Check if theme exists
 * @param {string} themeName - Theme identifier
 * @returns {boolean} True if theme exists
 */
export const isValidTheme = (themeName) => {
  return themeName && themes.hasOwnProperty(themeName);
};

/**
 * Default theme
 */
export const DEFAULT_THEME = 'aurora';
